// react
import { useMemo } from 'react';

// api
import {
  useEnergyTimestampsSummaryAllEnergyTimestampsSummaryAllAccountIdEnergyGroupIdGetQuery,
  useEnergyTimestampsSummaryEnergyTimestampsSummaryAccountIdEnergyGroupIdGetQuery,
} from '@/api/generated/energyAnalysis/endpoints/energyData';

// relative
import {
  evaluateSummary,
  getDescendantIds,
  mergeSummaries,
  type TimeRangeMillis,
  toSummaryMap,
} from './timestampsSummaryEvaluation';

type Scope = 'summary' | 'all' | 'node' | 'seu';

type TreeNodeLite = {
  energyTreeNodeId: string;
  parentId: string | null;
  type?: string | null;
};

type UseTimestampSummaryEvaluationArgs = {
  accountId?: number | null;
  energyGroupId?: number | null;
  scope: Scope;
  compareRange?: TimeRangeMillis | null;
  treeNodes?: TreeNodeLite[];
  nodeId?: string | null;
  seuType?: string | null;
  includeChildrenInWarning?: boolean;
  includeAllChildrenInWarning?: boolean;
};

export function useTimestampSummaryEvaluation({
  accountId,
  energyGroupId,
  scope,
  compareRange,
  treeNodes = [],
  nodeId,
  seuType,
  includeChildrenInWarning = false,
  includeAllChildrenInWarning = false,
}: UseTimestampSummaryEvaluationArgs) {
  const hasRequiredParams = Boolean(accountId && energyGroupId);

  const { data: summaryData, isFetching: isSummaryFetching } =
    useEnergyTimestampsSummaryEnergyTimestampsSummaryAccountIdEnergyGroupIdGetQuery(
      {
        accountId: accountId as number,
        energyGroupId: energyGroupId as number,
      },
      {
        skip: !hasRequiredParams || scope !== 'summary',
      },
    );

  const { data: allData, isFetching: isAllFetching } =
    useEnergyTimestampsSummaryAllEnergyTimestampsSummaryAllAccountIdEnergyGroupIdGetQuery(
      {
        accountId: accountId as number,
        energyGroupId: energyGroupId as number,
      },
      {
        skip: !hasRequiredParams || scope === 'summary',
      },
    );

  const summaryMap = useMemo(() => toSummaryMap(allData), [allData]);

  const selectedNodeIds = useMemo(() => {
    if (scope !== 'node' || !nodeId) {
      return [] as string[];
    }

    const normalizedNodeId = String(nodeId);
    const ids = new Set<string>([normalizedNodeId]);

    if (includeChildrenInWarning) {
      treeNodes
        .filter((node) => String(node.parentId) === normalizedNodeId)
        .forEach((node) => ids.add(String(node.energyTreeNodeId)));
    }

    if (includeAllChildrenInWarning) {
      getDescendantIds(normalizedNodeId, treeNodes).forEach((id) =>
        ids.add(id),
      );
    }

    return Array.from(ids);
  }, [
    scope,
    nodeId,
    treeNodes,
    includeChildrenInWarning,
    includeAllChildrenInWarning,
  ]);

  const selectedSummary = useMemo(() => {
    if (!hasRequiredParams) {
      return null;
    }

    if (scope === 'summary') {
      return summaryData ?? null;
    }

    if (scope === 'all') {
      return mergeSummaries(
        (allData ?? []).map((entry) => entry.timestampsSummary),
      );
    }

    if (scope === 'node') {
      const nodeSummaries = selectedNodeIds.map((id) => summaryMap[id]);
      return mergeSummaries(nodeSummaries);
    }

    if (scope === 'seu') {
      const seuSummaries = treeNodes
        .filter((node) => node.type === seuType)
        .map((node) => summaryMap[String(node.energyTreeNodeId)]);
      return mergeSummaries(seuSummaries);
    }

    return null;
  }, [
    hasRequiredParams,
    scope,
    summaryData,
    allData,
    selectedNodeIds,
    summaryMap,
    treeNodes,
    seuType,
  ]);

  const evaluation = useMemo(
    () => evaluateSummary(selectedSummary, compareRange),
    [selectedSummary, compareRange],
  );

  return {
    summary: selectedSummary,
    summaryMap,
    selectedNodeIds,
    displayWarning: evaluation.isOutOfBounds,
    adjustedRange: evaluation.adjustedRange,
    isSummaryInverted: evaluation.isInverted,
    isSummaryNoData: evaluation.isNoData,
    isValidSummary: evaluation.isValid,
    isFetching: isSummaryFetching || isAllFetching,
  };
}
